import { useEffect, useRef } from 'react';

// There are similar implementations on the web but:
// https://github.com/willmcpo/body-scroll-lock works for body lock while we want to lock scroll on parent;
// https://github.com/aliatsis/prevent-parent-scroll doesn't work for iOS.
// Neither use rounded values for bottom overscroll check.
function lockOverscroll(targetElement) {
  let initialClientY = -1;

  function isOverscrolled(delta) {
    return (
      // if scrolling down and reached the bottom.
      // In some cases scrollHeight, scrollTop, clientHeight may have fractional value.
      // For example when page is zoomed. To take it into account we use rounded values and add 1 to clientHeight.
      (delta > 0 &&
        Math.floor(targetElement.scrollHeight) - Math.ceil(targetElement.scrollTop) <=
          Math.ceil(targetElement.clientHeight) + 1) ||
      // if scrolling up and reached the top:
      (delta < 0 && targetElement.scrollTop === 0)
    );
  }

  function onMouseWheel(event) {
    // overscroll was handled by nested element
    if (event.overscrollHandled) return;

    event.overscrollHandled = true;

    if (isOverscrolled(event.deltaY)) event.preventDefault();
  }

  function onTouchStart(event) {
    if (event.targetTouches.length === 1) {
      initialClientY = event.targetTouches[0].clientY;
    }
  }

  function onTouchMove(event) {
    // overscroll was handled by nested element
    if (event.overscrollHandled) return;

    event.overscrollHandled = true;

    if (event.targetTouches.length === 1) {
      const delta = initialClientY - event.targetTouches[0].clientY;

      if (isOverscrolled(delta)) event.preventDefault();
    }
  }

  // most browsers support wheel event https://caniuse.com/#search=wheel
  targetElement.addEventListener('wheel', onMouseWheel, false);
  // Safari on iOS doesn't support wheel event, so we use touchmove:
  targetElement.addEventListener('touchstart', onTouchStart, false);
  targetElement.addEventListener('touchmove', onTouchMove, false);

  return function unlock() {
    targetElement.removeEventListener('wheel', onMouseWheel);
    targetElement.removeEventListener('touchstart', onTouchStart);
    targetElement.removeEventListener('touchmove', onTouchMove);
  };
}

// The simplest way to prevent parent scroll is to add css property 'overscroll-behavior: contain;'.
// As of now it is supported in Chrome and Firefox.
// For other browsers we prevent propagation of 'wheel' and 'touchmove' events.
export function useOverscrollLock(ref, dependencies = []) {
  const fallbackRef = useRef();

  useEffect(() => {
    const targetElement = ref ? ref.current : fallbackRef.current;

    if (!targetElement) return undefined;

    return lockOverscroll(targetElement);
  }, dependencies);

  return ref || fallbackRef;
}
